import React, { useState, forwardRef } from "react";
import { Icon } from "@/components/Icon";
import { useToast } from "@/context/ToastContext";
import { useTranslation } from "react-i18next";

export interface PasswordInputProps
    extends React.InputHTMLAttributes<HTMLInputElement> {
    hasError?: boolean;
    icon?:
        | React.ComponentType<{ size?: number; className?: string }>
        | React.ReactNode;
    defaultShowPassword?: boolean;
    label?: React.ReactNode;
    requiredLabel?: boolean;
    labelClassName?: string;
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
    (
        {
            hasError = false,
            icon,
            defaultShowPassword = false,
            label,
            requiredLabel = false,
            labelClassName = "",
            className = "",
            ...props
        },
        ref
    ) => {
        const { showError } = useToast();
        const { t } = useTranslation("global");
        const [showPassword, setShowPassword] =
            useState<boolean>(defaultShowPassword);

        const toggleShowPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setShowPassword(!showPassword);
        };

        return (
            <div className="w-full">
                {label && (
                    <label
                        className={`block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 ${labelClassName}`}
                    >
                        {label} {props.required && requiredLabel && "*"}
                    </label>
                )}
                <div className="relative w-full">
                    {icon && (
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                            {typeof icon === "function"
                                ? React.createElement(icon, { size: 15 })
                                : icon}
                        </div>
                    )}

                    <input
                        ref={ref}
                        type={showPassword ? "text" : "password"}
                        className={`w-full bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl py-2.5 ${
                            icon ? "pl-10" : "pl-3.5"
                        } pr-10 text-xs font-medium focus:outline-none focus:ring-0 focus:border-slate-300 outline-none transition-all shadow-xs ${
                            hasError
                                ? "!border-rose-400 !bg-rose-50/50 !text-rose-900"
                                : ""
                        } ${className}`}
                        onInvalid={(e) => {
                            e.preventDefault();
                            if (
                                props.required &&
                                !(e.target as HTMLInputElement).value
                            ) {
                                showError(t("errors.field_required"));
                            }
                            if (props.onInvalid) {
                                props.onInvalid(e);
                            }
                        }}
                        {...props}
                    />

                    <button
                        type="button"
                        onClick={toggleShowPassword}
                        tabIndex={-1}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer focus:outline-none"
                    >
                        {showPassword ? (
                            <Icon value="eyeOff" size={16} />
                        ) : (
                            <Icon value="eye" size={16} />
                        )}
                    </button>
                </div>
            </div>
        );
    }
);

PasswordInput.displayName = "PasswordInput";
export default PasswordInput;
